import { searchLicenses } from './api';
import { classifyInput } from './classifier';
import type { SearchResult, SmartSearchResponse } from './types';

// DOM Elements
const form = document.getElementById('search-form') as HTMLFormElement;
const input = document.getElementById('search-input') as HTMLInputElement;
const hint = document.getElementById('input-hint') as HTMLParagraphElement;
const searchBtn = document.getElementById('search-btn') as HTMLButtonElement;
const resultsSection = document.getElementById('results') as HTMLElement;
const noResultsSection = document.getElementById('no-results') as HTMLElement;
const suggestionsSection = document.getElementById('suggestions') as HTMLElement;
const dbprLink = document.getElementById('dbpr-link') as HTMLAnchorElement;
const errorSection = document.getElementById('error') as HTMLElement;

// Input hint as user types
input.addEventListener('input', () => {
    const classification = classifyInput(input.value);
    if (classification) {
        hint.textContent = `Searching as: ${classification}`;
        hint.classList.remove('hidden');
    } else {
        hint.classList.add('hidden');
    }
});

// Form submission
form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const query = input.value.trim();
    if (!query) return;

    setLoading(true);
    hideAllResults();

    try {
        const response = await searchLicenses(query);
        renderResults(response);
    } catch (error) {
        showError(error instanceof Error ? error.message : 'Search failed. Please try again.');
    } finally {
        setLoading(false);
    }
});

function renderResults(response: SmartSearchResponse) {
    // Update DBPR fallback link
    dbprLink.href = response.dbpr_fallback_url;

    if (response.results.length === 0) {
        // Show no results and suggestions
        noResultsSection.classList.remove('hidden');

        if (response.suggestions.length > 0) {
            suggestionsSection.innerHTML = '<h4>Suggestions:</h4><ul>' +
                response.suggestions.map(s => `<li>${escapeHtml(s)}</li>`).join('') +
                '</ul>';
            suggestionsSection.classList.remove('hidden');
        }
        return;
    }

    // Render result cards
    resultsSection.innerHTML = `
        <p class="results-count">Found ${response.results.length} license${response.results.length !== 1 ? 's' : ''}</p>
        <div class="results-grid">
            ${response.results.map(renderResultCard).join('')}
        </div>
    `;
    resultsSection.classList.remove('hidden');
}

function renderResultCard(result: SearchResult): string {
    const statusClass = result.status_ok === true
        ? 'status-ok'
        : result.status_ok === false
            ? 'status-warn'
            : '';

    return `
        <div class="result-card">
            <div class="result-header">
                <h3>${escapeHtml(result.name)}</h3>
                <span class="license-number">${escapeHtml(result.license_number)}</span>
            </div>
            <div class="result-details">
                <div class="detail-row">
                    <span class="label">Type:</span>
                    <span class="value">${escapeHtml(result.license_type)}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Status:</span>
                    <span class="value ${statusClass}">${escapeHtml(result.status || 'Unknown')}</span>
                </div>
                ${result.expiration_date ? `
                <div class="detail-row">
                    <span class="label">Expires:</span>
                    <span class="value">${escapeHtml(result.expiration_date)}</span>
                </div>
                ` : ''}
                ${result.county ? `
                <div class="detail-row">
                    <span class="label">County:</span>
                    <span class="value">${escapeHtml(result.county)}</span>
                </div>
                ` : ''}
            </div>
        </div>
    `;
}

// Loading state management with engaging UX
let loadingMessages: ReturnType<typeof setInterval> | null = null;
let loadingStartTime: number = 0;

interface LoadingMessage {
    time: number;
    text: string;
    subtext: string | null;
    showEscape?: boolean;
}

const LOADING_MESSAGES: LoadingMessage[] = [
    { time: 0, text: "Checking our local database...", subtext: null },
    { time: 2000, text: "Querying Florida DBPR...", subtext: "This searches the official state database" },
    { time: 6000, text: "Still searching...", subtext: "DBPR can be slow sometimes" },
    { time: 12000, text: "Almost there...", subtext: "Complex searches take a moment" },
    { time: 20000, text: "This is taking a while", subtext: null, showEscape: true },
    { time: 35000, text: "Hang tight, we're still working on it", subtext: null, showEscape: true },
];

function setLoading(loading: boolean) {
    searchBtn.disabled = loading;

    if (loading) {
        loadingStartTime = Date.now();
        searchBtn.textContent = 'Searching...';
        searchBtn.classList.add('loading');
        showLoadingOverlay();
        startLoadingMessages();
    } else {
        searchBtn.textContent = 'Search';
        searchBtn.classList.remove('loading');
        hideLoadingOverlay();
        stopLoadingMessages();
    }
}

function showLoadingOverlay() {
    // Remove existing overlay if any
    hideLoadingOverlay();

    const overlay = document.createElement('div');
    overlay.id = 'loading-overlay';
    overlay.innerHTML = `
        <div class="loading-content">
            <div class="loading-spinner">
                <div class="spinner-ring"></div>
                <div class="spinner-ring"></div>
                <div class="spinner-ring"></div>
            </div>
            <p id="loading-message" class="loading-message">Checking our local database...</p>
            <p id="loading-subtext" class="loading-subtext"></p>
            <div id="loading-escape" class="loading-escape hidden">
                <p>Think you can find it faster?</p>
                <a href="https://www.myfloridalicense.com/wl11.asp?mode=0&SID=" target="_blank" rel="noopener" class="escape-link">
                    Search DBPR directly →
                </a>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    // Fade in
    requestAnimationFrame(() => {
        overlay.classList.add('visible');
    });
}

function hideLoadingOverlay() {
    const overlay = document.getElementById('loading-overlay');
    if (overlay) {
        overlay.classList.remove('visible');
        setTimeout(() => overlay.remove(), 200);
    }
}

function startLoadingMessages() {
    stopLoadingMessages();

    const updateMessage = () => {
        const elapsed = Date.now() - loadingStartTime;
        const messageEl = document.getElementById('loading-message');
        const subtextEl = document.getElementById('loading-subtext');
        const escapeEl = document.getElementById('loading-escape');

        if (!messageEl) return;

        // Find the appropriate message for elapsed time
        let currentMessage = LOADING_MESSAGES[0];
        for (const msg of LOADING_MESSAGES) {
            if (elapsed >= msg.time) {
                currentMessage = msg;
            }
        }

        messageEl.textContent = currentMessage.text;

        if (subtextEl) {
            subtextEl.textContent = currentMessage.subtext || '';
            subtextEl.classList.toggle('hidden', !currentMessage.subtext);
        }

        if (escapeEl) {
            escapeEl.classList.toggle('hidden', !currentMessage.showEscape);
        }
    };

    updateMessage();
    loadingMessages = setInterval(updateMessage, 1000);
}

function stopLoadingMessages() {
    if (loadingMessages) {
        clearInterval(loadingMessages);
        loadingMessages = null;
    }
}

function hideAllResults() {
    resultsSection.classList.add('hidden');
    noResultsSection.classList.add('hidden');
    suggestionsSection.classList.add('hidden');
    errorSection.classList.add('hidden');
}

function showError(message: string) {
    // If API is down, show helpful message with direct DBPR link
    const isConnectionError = message.includes('fetch') || message.includes('network') || message.includes('Failed');

    if (isConnectionError) {
        errorSection.innerHTML = `
            <p>Our search service is temporarily unavailable.</p>
            <p style="margin-top: 12px;">
                <a href="https://www.myfloridalicense.com/wl11.asp?mode=0&SID="
                   target="_blank"
                   rel="noopener"
                   style="color: #2563eb; text-decoration: underline; font-weight: 500;">
                   Search directly on Florida's official DBPR website
                </a>
            </p>
        `;
    } else {
        errorSection.innerHTML = `<p>${escapeHtml(message)}</p>`;
    }
    errorSection.classList.remove('hidden');
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// ============================================
// Rotating Fraud Stats - Clickable with Sources
// ============================================
interface FraudStat {
    text: string;
    url: string;
}

const fraudStats: FraudStat[] = [
    {
        text: 'Florida ranks #1 for fraud per capita, with $866 million in losses reported in 2024.',
        url: 'https://www.synovus.com/personal/resource-center/fraud-prevention-and-security-hub/fraud-hub-education-and-prevention/featured/fraud-in-florida-and-georgia/'
    },
    {
        text: 'Insurance fraud cases against Florida contractors nearly doubled from 2019 to 2021.',
        url: 'https://www.conroysimberg.com/blog/contractor-fraud-and-abuse-negatively-impacting-the-florida-insurance-industry/'
    },
    {
        text: '51% of Floridians displaced by hurricanes were targeted by contractor scams.',
        url: 'https://floridadaily.com/florida-more-than-half-of-disaster-displaced-residents-targeted-by-scammers/'
    },
    {
        text: 'Contractor fraud is a "billion-dollar problem" in Florida.',
        url: 'https://www.news4jax.com/news/local/2024/11/11/a-billion-dollar-problem-contractor-fraud-is-rampant-in-florida-heres-how-to-protect-yourself/'
    },
    {
        text: 'Unlicensed contracting is a 3rd-degree felony in Florida.',
        url: 'https://www.leg.state.fl.us/statutes/index.cfm?App_mode=Display_Statute&URL=0400-0499/0489/Sections/0489.127.html'
    },
    {
        text: '100+ arrested for unlicensed contracting after Hurricane Milton in 2024.',
        url: 'https://www.insurancejournal.com/news/southeast/2024/10/28/798784.htm'
    },
    {
        text: '60+ arrested in a 3-day undercover sting targeting unlicensed contractors.',
        url: 'https://baynews9.com/fl/tampa/news/2024/11/08/unlicensed-contractors-crackdown-in-pinellas-nets-dozens-of-arrests'
    },
    {
        text: 'Florida CFO: "Help comes within days, predators come within hours."',
        url: 'https://myfloridacfo.com/division/ica/demolish'
    },
];

const rotatingStat = document.getElementById('rotating-stat');
const rotatingStatLink = document.getElementById('rotating-stat-link') as HTMLAnchorElement;
let statIndex = 0;

// Initialize first stat link
if (rotatingStatLink) {
    rotatingStatLink.href = fraudStats[0].url;
}

function rotateStat() {
    if (!rotatingStat || !rotatingStatLink) return;

    rotatingStat.style.opacity = '0';

    setTimeout(() => {
        statIndex = (statIndex + 1) % fraudStats.length;
        const stat = fraudStats[statIndex];
        rotatingStat.textContent = stat.text;
        rotatingStatLink.href = stat.url;
        rotatingStat.style.opacity = '1';
    }, 400);
}

// Rotate stats every 6 seconds
setInterval(rotateStat, 6000);
